import { makeAutoObservable } from "mobx";
import { IPromiseBasedObservable, fromPromise } from "mobx-utils";
import {
  PatientsApi,
  PatientDto,
  AddressDto,
  RxDto,
  PatientMinimalDto,
  InsurerDto,
} from "../../api-client";
import {
  isSearchParam,
  searchRoute,
} from "../../helpers/searchParams/searchParamDecorator";
import AuthStore from "../Auth/authStore";
import notificationStore from "../Notification/notificationStore";
import UserStore from "../User/UserStore";
import { toRxWriteDto } from "../../helpers/rxWriteDto";

interface RoutingParams {
  pId: any;
  rxId?: any;
  selectedSubTab?: any;
  doctorReportId?: string | null;
  newdoctorReportId?: string | null;
}

export enum PATIENT_SUBTABS {
  PRESCRIPTIONS = "prescriptions",
  APPOINTMENTS = "appointments",
  BILLING = "billing",
}

interface OpenPatient extends PatientMinimalDto {
  selectedRxId?: string | null;
}

export interface PreflightState {
  open: boolean;
  seriesIds: string[];
  manual?: boolean;
}

export interface CopaymentExemptionDialogState {
  open: boolean;
  patientId: number | null;
}

export interface InsuranceDataLiftoverDialogState {
  open: boolean;
  patientId: number | null;
  rxInsureeNumber: string | null;
  patientInsuranceNo: string | null;
  rxPayer: InsurerDto | null;
  patientPayer: InsurerDto | null;
  rxInsuranceStatus: string | null;
  patientInsuranceStatus: string | null;
}

@searchRoute("/patient")
export default class PatientsStore {
  public apiClient: PatientsApi;
  public authStore: AuthStore;
  public userStore: UserStore; // Include UserStore as a property

  public constructor(
    apiClient: PatientsApi,
    userStore: UserStore,
    authStore: AuthStore,
  ) {
    makeAutoObservable(this, {
      apiClient: false,
      authStore: false,
      userStore: false,
    });
    this.apiClient = apiClient;
    this.authStore = authStore;
    this.userStore = userStore;
  }
  // the currently activated patient
  @isSearchParam<number>()
  public selectedPatientId: number | undefined = undefined;

  @isSearchParam<string | undefined>()
  public selectedRxId: string | null = null;

  @isSearchParam<string | undefined>()
  public selectedSeriesId: string | undefined = undefined;
  public openPatients: OpenPatient[] = []; // the patients that are open in the tabs

  public preflightDialogOpen: PreflightState = {
    open: false,
    seriesIds: [],
  };

  public copaymentExemptionDialogOpen: CopaymentExemptionDialogState = {
    open: false,
    patientId: null,
  };

  public insuranceDataLiftoverDialogOpen: InsuranceDataLiftoverDialogState =
    {
      open: false,
      patientId: null,
      rxInsureeNumber: null,
      patientInsuranceNo: null,
      rxPayer: null,
      patientPayer: null,
      rxInsuranceStatus: null,
      patientInsuranceStatus: null,
    };
  // Current patient tab index (0 = new person tab, 1+ = patient tabs)
  // public patientTabIndex: number = 0;

  // Subtabs for the right panel
  @isSearchParam<string | undefined>()
  public selectedSubTabStr: string | undefined = PATIENT_SUBTABS.PRESCRIPTIONS;
  public showSubTabs: boolean = false;

  setSelectedPatientId = (id: number | undefined) => {
    // console.debug("!!!!!!!!Setting selectedPatientId to", id);

    if (this.selectedPatientId !== id) {
      this.selectedPatientId = id;
      this.selectedSeriesId = undefined;
    }

    // if we have a selectedRx in the open dtos, we set it here
    const openPatient = this.openPatients.find((p) => p.id === id);
    if (openPatient?.selectedRxId) {
      this.selectedRxId = openPatient.selectedRxId;
    } else {
      this.selectedRxId = null;
    }
  };

  // updates the open patients, presevering any rx selections that were made
  setOpenPatients = (
    patients: PatientMinimalDto[],
    newPatient?: PatientMinimalDto,
  ) => {
    const newOpenPatients = patients.map((p) => {
      const selectedRxId = this.openPatients.find(
        (op) => op.id === p.id,
      )?.selectedRxId;
      return selectedRxId ? { ...p, selectedRxId } : p;
    });
    this.openPatients = newOpenPatients;
  };

  setSelectedSubTabStr = (tab: string) => {
    this.selectedSubTabStr = tab;
  };

  public setPreflightDialogOpen = (open: boolean, seriesIds: string[], manual = false) => {
    // console.log("SETTING PREFLIGHT DIALOG OPEN TO", open, seriesIds);
    this.preflightDialogOpen.open = open;
    this.preflightDialogOpen.seriesIds = seriesIds;
    this.preflightDialogOpen.manual = manual;
  };

  public setCopaymentExemptionDialogOpen = (
    open: boolean,
    patientId: number | null,
  ) => {
    this.copaymentExemptionDialogOpen = { open, patientId };
  };

  public setInsuranceDataLiftoverDialogOpen = (
    open: boolean,
    patientId: number | null,
    rxInsureeNumber: string | null = null,
    patientInsuranceNo: string | null = null,
    rxPayer: InsurerDto | null = null,
    patientPayer: InsurerDto | null = null,
    rxInsuranceStatus: string | null = null,
    patientInsuranceStatus: string | null = null,
  ) => {
    this.insuranceDataLiftoverDialogOpen = {
      open,
      patientId,
      rxInsureeNumber,
      patientInsuranceNo,
      rxPayer,
      patientPayer,
      rxInsuranceStatus,
      patientInsuranceStatus,
    };
  };

  addToTabs = (patient: PatientMinimalDto | PatientDto) => {
    // at the moment we only add patients if they are not already present
    if (!this.openPatients.find((p) => p.id === patient.id)) {
      this.openPatients = [
        ...this.openPatients,
        {
          id: patient.id,
          firstName: patient.firstName!,
          lastName: patient.lastName!,
          dob: patient.dob!,
        },
      ];
    }
  };

  setSelectedRxId = (id: string | null) => {
    this.selectedRxId = id;
    if (id) this.selectedSeriesId = undefined;

    // also update the openPatients entry
    this.openPatients = this.openPatients.map((p) => {
      if (p.id === this.selectedPatientId) {
        return { ...p, selectedRxId: id };
      }
      return p;
    });
  };

  setSelectedSeriesId = (id: string | undefined) => {
    this.selectedSeriesId = id;
  };

  // public patientSubTabs: Array<Array<any>> = [];
  // public isOpen: boolean = false;
  // public selectedSubTab: any = "";

  public constructSearchPath = (params: RoutingParams) => {
    return "/patient";
  };

  // Method to add or update selected patient route
  public updateSelectedPatientRoute = (routingParams: RoutingParams) => {
    return undefined;
  };

  public getSelectedPatientparams = (patientId: any) => {
    // if (patientId) {
    //   const patient: any = this.selectedPatientsRoute.find(
    //     (patient: any) => patient.pId.toString() === patientId.toString()
    //   );
    //   console.log("SelectedRouttre", this.selectedPatientsRoute, patient, patientId);
    //   return patient;
    // }
    return { pId: patientId };
  };

  public setShowSubTabs = (value: boolean) => {
    this.showSubTabs = value;
  };

  // ... Other existing code ...

  // @action
  // public setMasterPatientData = (options: PatientDto) => {
  //   this.masterPatientData = options;
  // };
  public resourceRequest: IPromiseBasedObservable<any> | null = null;

  /*
  public getPatients = async () => {
    const { data } = await this.apiClient.getPatients() as any;
    return data || [];
  };

  */
  public getAllPatients = async () => {
    const { data } = (await this.apiClient.getAllPatients()) as any;
    return data || [];
  };

  public getRxStats = async () => {
    const { data } = (await this.apiClient.getRxStats()) as any;
    return data || [];
  };

  
  public getPatientByRxId = async (rxId: string) => {
    try {
      const { data } = (await this.apiClient.getPatientByRxId(rxId)) as any;

      // this.setPatientData(data)
      // this.setMasterPatientData(data)

      // notifySuccess(`Der Patient wurde erfolgreich aktualisiert.`);
      return data || [];
    } catch (error) {
      this.resourceRequest = fromPromise.reject(error);
      notificationStore.showMessage(
        "Patientendaten konnten nicht abgerufen werden!",
        "error",
        error,
      );
      // notifyError('Failed')
    }
  };

  public deletePatientAddres = async (addressId: string) => {
    try {
      const response = await this.apiClient.deleteAddress(addressId);

      const data = response.data as AddressDto;

      return data;
    } catch (error) {
      this.resourceRequest = fromPromise.reject(error);
      notificationStore.showMessage(
        "Fehler beim Löschen der Patientenadresse",
        "error",
        error,
      );
      // notifyError('Failed')
    }
  };

  public getLiveValidation = async (rx: RxDto) => {
    try {
      // console.log("VALUES LETS SEEEEEEEEEEEEEEEE", rx);

      // Remove empty strings; for GKV Rxs also remove partial (<9 char) entries that would fail class validator
      const rxTypeText = rx.rxtype?.text || "";
      const isNonGKV = ["Privat/Selbstzahler", "Berufsgenossenschaft", "Bundeswehr"].includes(rxTypeText);
      if (rx.doctorId === "") {
        rx.doctorId = undefined;
      } else if (!isNonGKV && rx.doctorId && rx.doctorId.length < 9) {
        rx.doctorId = undefined;
      }
      if (rx.clinicId === "") {
        rx.clinicId = undefined;
      } else if (!isNonGKV && rx.clinicId && rx.clinicId.length < 9) {
        rx.clinicId = undefined;
      }

      const { data } = (await this.apiClient.getLiveValidation(toRxWriteDto(rx))) as any;
      // console.log("LIVE VALIDATION", data);
      return data || [];
    } catch (error) {
      this.resourceRequest = fromPromise.reject(error);
      notificationStore.showMessage(
        "Fehler bei der Patientenaktualisierung",
        "error",
        error,
      );
      // notifyError('Failed')
    }
  };

  // Archive-related methods
  public getArchivedPatients = async (
    page: number = 1,
    limit: number = 50,
    search?: string,
  ) => {
    try {
      const { data } = (await this.apiClient.getArchivedPatients(
        page,
        limit,
        search,
      )) as any;
      return data || { patients: [], total: 0, page: 1, limit: 50 };
    } catch (error) {
      this.resourceRequest = fromPromise.reject(error);
      notificationStore.showMessage(
        "Fehler beim Abrufen der archivierten Patienten",
        "error",
        error,
      );
      return { patients: [], total: 0, page: 1, limit: 50 };
    }
  };

  public restorePatient = async (patientId: number) => {
    try {
      const { data } = (await this.apiClient.restorePatient(patientId)) as any;
      notificationStore.showMessage(
        "Patient erfolgreich wiederhergestellt",
        "success",
      );
      return data || {};
    } catch (error) {
      this.resourceRequest = fromPromise.reject(error);
      notificationStore.showMessage(
        "Fehler beim Wiederherstellen des Patienten",
        "error",
        error,
      );
      throw error;
    }
  };

  public permanentlyDeletePatient = async (
    patientId: number,
    confirmationText: string,
  ) => {
    try {
      const { data } = (await this.apiClient.hardDeletePatient(patientId, {
        confirmationText,
      })) as any;
      notificationStore.showMessage("Patient endgültig gelöscht", "success");
      return data || {};
    } catch (error) {
      this.resourceRequest = fromPromise.reject(error);
      const errorMessage =
        (error as any)?.response?.data?.message ||
        "Fehler beim endgültigen Löschen des Patienten";
      notificationStore.showMessage(errorMessage, "error", error);
      throw error;
    }
  };

  public archivePatient = async (patientId: number) => {
    try {
      // API-Aufruf über axios direkt
      const axios = (await import("axios")).default;
      const baseURL = process.env.REACT_APP_API_URL || "http://localhost:3003";

      // API-Aufruf mit konfiguriertem Axios
      const response = await axios({
        method: "delete", // Geändert von 'patch' zu 'delete' um mit dem Backend-Controller übereinzustimmen
        url: `/patient/softDelete/${patientId}`,
        baseURL,
        headers: {
          Authorization: `Bearer ${this.authStore.authToken}`,
        },
      });

      const data = response.data;
      notificationStore.showMessage(
        "Patient erfolgreich archiviert",
        "success",
      );
      return data || {};
    } catch (error) {
      this.resourceRequest = fromPromise.reject(error);
      notificationStore.showMessage(
        "Fehler beim Archivieren des Patienten",
        "error",
        error,
      );
      throw error;
    }
  };

  /**
   * Navigation Pattern (see navigateWithProjection helper):
   *
   * import { navigateToPatient } from '../../helpers/searchParams/navigateWithProjection';
   * const onPatientClick = () => {
   *   navigateToPatient(patientsStore, navigate, timeSlot.patient?.id, 'appointments');
   * };
   *
   * If the patient store on the target page has already hydrated, this will simply mutate the store
   * (triggering the projection effect to update the URL) and navigate to /patient without query string noise.
   * If hydration has not yet occurred (first transition in a fresh session), it will encode the selection
   * into the query string so initial hydration can pick it up.
   */

  // Required in future
  // @action
  // public searchPatients = async (text: string) => {
  //   const { data } = await this.apiClient.searchPatients(text) as any;
  //   return data || [];
  // };
}
